// 매출 대시보드 전용 임시 verse 세션 풀.
//
// 어드민의 활성 verse 연결(AdminRPCClient)은 한 번에 한 verse 만 붙는데, 매출 합산 조회는
// prod-w2/prod-w3 양쪽 payer 를 함께 보여준다. 닉네임을 조회하려면 payer 가 속한 verse 에
// 붙어야 하므로, 활성 연결을 건드리지 않고 조회용 세션을 verse 별로 따로 열고 닫는다.

import { createV8Session } from '@rb/v8-client/session';
import type { V8Session } from '@rb/v8-client';
import type { UserStateFetcher } from '@/shop/game-nickname';

export interface VerseSessionPoolParams {
  /** 어드민 지갑 주소 (연결 주체). */
  account: string;
  /** V8 JWT provider — 세션 재연결 시마다 호출된다. */
  getToken: () => string | null;
  /** verse env 에 해당하는 gateway URL. */
  gatewayUrl: string;
}

export interface VerseSessionPool {
  /** verse 별 유저 상태 조회 — getUserDetail 의 state 만 꺼내 준다. */
  fetchState: UserStateFetcher;
  /** 열린 세션 전부 종료. 조회가 끝나면 반드시 호출. */
  close: () => Promise<void>;
}

export function createVerseSessionPool(params: VerseSessionPoolParams): VerseSessionPool {
  const sessions = new Map<string, Promise<V8Session>>();

  const open = (verseId: string): Promise<V8Session> => {
    const existing = sessions.get(verseId);
    if (existing) return existing;
    const created = createV8Session({
      config: {
        account: params.account,
        verse: verseId,
        env: 'prod', // 등록 verse 는 전부 인증 필요 — preview 는 대상 아님
        remoteUrl: params.gatewayUrl,
      },
      bearerProvider: async () => {
        const token = params.getToken();
        if (!token) throw new Error('[verse-session-pool] 로그인 토큰이 없습니다');
        return token;
      },
    });
    sessions.set(verseId, created);
    return created;
  };

  const fetchState: UserStateFetcher = async (verseId, account) => {
    const session = await open(verseId);
    const detail = (await session.server.remoteFunction('getUserDetail', [account])) as {
      state: Record<string, unknown> | null;
    } | null;
    return detail?.state ?? null;
  };

  const close = async (): Promise<void> => {
    const opened = [...sessions.values()];
    sessions.clear();
    for (const p of opened) {
      try {
        const session = await p;
        await session.shutdown();
      } catch {
        // 종료 실패는 무시 — 조회 결과에 영향 없음.
      }
    }
  };

  return { fetchState, close };
}
